import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Chip,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    SelectChangeEvent
} from '@mui/material';
import { useDataProvider, useNotify } from 'react-admin';

interface Category {
    id: number;
    name: string;
    color: string;
}

interface MediaFile {
    id: number;
    uuid: string;
    originalFilename: string;
    description?: string;
    categories?: (Category | string)[];
}

interface FileEditDialogProps {
    open: boolean;
    file: MediaFile | null;
    onClose: () => void;
    onUpdate: () => void;
    allCategories: Category[];
}

const FileEditDialog: React.FC<FileEditDialogProps> = ({
    open,
    file,
    onClose,
    onUpdate,
    allCategories
}) => {
    const [description, setDescription] = useState('');
    const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
    const dataProvider = useDataProvider();
    const notify = useNotify();

    useEffect(() => {
        if (file) {
            setDescription(file.description || '');

            // Handle both category objects, IRIs, and plain ID strings
            const categoryIds = file.categories?.map(cat => {
                if (typeof cat === 'object' && cat.id) {
                    // Category object with id
                    return cat.id;
                } else if (typeof cat === 'string') {
                    // Could be an IRI like "/api/media_categories/1" or just "1"
                    if (cat.includes('/')) {
                        const match = cat.match(/\/(\d+)$/);
                        return match ? parseInt(match[1], 10) : undefined;
                    } else {
                        // Plain ID string like "1"
                        return parseInt(cat, 10);
                    }
                } else if (typeof cat === 'number') {
                    return cat;
                }
                return undefined;
            }).filter((id): id is number => id !== undefined && typeof id === 'number' && !isNaN(id)) || [];

            setSelectedCategoryIds(categoryIds);
        }
    }, [file]);

    const handleCategoryChange = (event: SelectChangeEvent<number[]>) => {
        const value = event.target.value;
        setSelectedCategoryIds(typeof value === 'string' ? [] : value);
    };

    const handleSave = async () => {
        if (!file) return;

        try {
            // Convert category IDs to IRIs for API Platform
            const categoryIRIs = selectedCategoryIds.map(id => `/api/media_categories/${id}`);

            await dataProvider.update('media_files', {
                id: file.id,
                data: {
                    description,
                    categories: categoryIRIs
                },
                previousData: file
            });

            notify('Datei aktualisiert', { type: 'success' });
            onUpdate();
            onClose();
        } catch (error) {
            notify('Fehler beim Aktualisieren', { type: 'error' });
        }
    };

    if (!file) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Datei bearbeiten</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <TextField
                        label="Dateiname"
                        value={file.originalFilename}
                        disabled
                        fullWidth
                    />

                    <TextField
                        label="Beschreibung"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                    />

                    <FormControl fullWidth>
                        <InputLabel>Kategorien</InputLabel>
                        <Select
                            multiple
                            value={selectedCategoryIds}
                            onChange={handleCategoryChange}
                            input={<OutlinedInput label="Kategorien" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((categoryId) => {
                                        const category = allCategories.find(c => c.id === categoryId);
                                        return category ? (
                                            <Chip
                                                key={category.id}
                                                label={category.name}
                                                size="small"
                                                sx={{
                                                    backgroundColor: category.color,
                                                    color: 'white'
                                                }}
                                            />
                                        ) : null;
                                    })}
                                </Box>
                            )}
                        >
                            {allCategories.map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                    <Chip
                                        label={category.name}
                                        size="small"
                                        sx={{
                                            backgroundColor: category.color,
                                            color: 'white',
                                            mr: 1
                                        }}
                                    />
                                    {category.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Abbrechen</Button>
                <Button onClick={handleSave} variant="contained">
                    Speichern
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FileEditDialog;
